import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, CheckCircle2, Save } from 'lucide-react';
import api from '../../../../api/client';
import { TemplatePayloadCaption } from './whatsappTemplateShared';

const FONT = { fontFamily: "'Poppins', sans-serif" };
const INPUT_CLASS =
  'w-full px-3 py-2.5 rounded-xl bg-gray-50/50 border border-gray-200 focus:bg-white focus:border-blue-500/30 focus:ring-2 focus:ring-blue-500/10 text-sm text-gray-700 font-medium';
const BTN_PRIMARY =
  'px-4 py-2 text-white rounded-xl font-bold text-sm hover:opacity-95 disabled:opacity-50 flex items-center gap-2';
const BTN_SECONDARY =
  'px-4 py-2 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-50';
const GRADIENT = 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)';

function formatApiError(err, fallback) {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.detail) return String(data.detail);
  if (data.error) return String(data.error);
  const parts = [];
  Object.entries(data).forEach(([key, val]) => {
    if (Array.isArray(val)) parts.push(`${key}: ${val[0]}`);
    else if (typeof val === 'string') parts.push(val);
  });
  return parts.length ? parts.join(' ') : fallback;
}

const emptyEditor = () => ({
  id: null,
  name: '',
  body_text: '',
  external_template_name: '',
  language: 'en',
  content_sid: '',
});

export default function WhatsAppEventTemplateModal({
  open,
  onClose,
  eventId,
  eventLabel,
  companyId,
  activeProvider,
  onChanged,
}) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [mode, setMode] = useState('pick');
  const [editor, setEditor] = useState(emptyEditor);

  const isTwilio = activeProvider === 'twilio';

  const fetchTemplates = useCallback(async () => {
    if (!open || !companyId || !activeProvider || !eventId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/whatsapp-templates/by_event/', {
        params: {
          event_type: eventId,
          provider: activeProvider,
          company_id: companyId,
        },
      });
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      setFormError(formatApiError(err, t('whatsappTemplates.messages.loadFailed')));
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [open, companyId, activeProvider, eventId, t]);

  useEffect(() => {
    if (open) {
      setMode('pick');
      setEditor(emptyEditor());
      setFormError('');
      fetchTemplates();
    }
  }, [open, fetchTemplates]);

  const handleSetActive = async (id) => {
    try {
      await api.post(`/whatsapp-templates/${id}/set_active/`, { company_id: companyId });
      await fetchTemplates();
      onChanged?.();
      onClose();
    } catch (err) {
      setFormError(formatApiError(err, t('whatsappTemplates.messages.activateFailed')));
    }
  };

  const validateForm = () => {
    if (!editor.name.trim()) return t('whatsappTemplates.validation.nameRequired');
    if (!editor.body_text.trim()) return t('whatsappTemplates.validation.bodyRequired');
    if (isTwilio && !editor.content_sid.trim()) {
      return t('whatsappTemplates.validation.contentSidRequired');
    }
    return '';
  };

  const handleCreateAndActivate = async () => {
    const validationMsg = validateForm();
    if (validationMsg) {
      setFormError(validationMsg);
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      const payload = {
        company_id: companyId,
        provider: activeProvider,
        event_type: eventId,
        name: editor.name.trim(),
        body_text: editor.body_text.trim(),
        external_template_name: editor.external_template_name.trim() || editor.name.trim(),
        language: editor.language.trim() || 'en',
        content_sid: editor.content_sid.trim(),
      };
      const { data: created } = await api.post('/whatsapp-templates/', payload);
      await api.post(`/whatsapp-templates/${created.id}/set_active/`, { company_id: companyId });
      await fetchTemplates();
      onChanged?.();
      onClose();
    } catch (err) {
      setFormError(formatApiError(err, t('whatsappTemplates.messages.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={FONT}>
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label={t('whatsappSettings.eventTemplateModal.close')}
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wa-event-template-title"
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-start justify-between gap-3 rounded-t-2xl">
          <div>
            <h2 id="wa-event-template-title" className="text-lg font-bold text-gray-900">
              {t('whatsappSettings.eventTemplateModal.title')}
            </h2>
            <p className="text-xs text-gray-500 font-medium mt-0.5">{eventLabel}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {formError && (
            <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
              {formError}
            </div>
          )}

          {mode === 'pick' ? (
            <>
              <p className="text-sm text-gray-600 mb-4">
                {t('whatsappSettings.eventTemplateModal.pickHint')}
              </p>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#0ea5e9]" />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-6 rounded-xl bg-gray-50 border border-dashed border-gray-200">
                  <p className="text-sm text-gray-600 mb-3">
                    {t('whatsappSettings.eventTemplateModal.noTemplates')}
                  </p>
                  <button
                    type="button"
                    onClick={() => setMode('create')}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold shadow-sm hover:shadow-md transition-all"
                    style={{ background: GRADIENT }}
                  >
                    <Plus className="w-4 h-4" />
                    {t('whatsappSettings.eventTemplateModal.createNew')}
                  </button>
                </div>
              ) : (
                <ul className="space-y-2 mb-4">
                  {templates.map((tpl) => (
                    <li
                      key={tpl.id}
                      className={`flex items-center justify-between gap-2 p-3 rounded-xl border ${
                        tpl.is_active
                          ? 'border-emerald-200 bg-emerald-50/50'
                          : 'border-gray-100 hover:bg-gray-50'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">{tpl.name}</p>
                        {tpl.is_active && (
                          <span className="text-[10px] font-bold uppercase text-emerald-700">
                            {t('whatsappTemplates.active')}
                          </span>
                        )}
                      </div>
                      {!tpl.is_active ? (
                        <button
                          type="button"
                          onClick={() => handleSetActive(tpl.id)}
                          className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                        >
                          {t('whatsappSettings.eventTemplateModal.useTemplate')}
                        </button>
                      ) : (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {templates.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setMode('create');
                    setFormError('');
                  }}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  {t('whatsappSettings.eventTemplateModal.createNew')}
                </button>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => {
                  setMode('pick');
                  setFormError('');
                }}
                className="text-xs font-bold text-[#6366f1] mb-3 hover:underline"
              >
                {t('whatsappSettings.eventTemplateModal.backToList')}
              </button>
              <TemplatePayloadCaption eventId={eventId} providerId={activeProvider} t={t} />
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    {t('whatsappTemplates.fields.name')} *
                  </label>
                  <input
                    className={INPUT_CLASS}
                    value={editor.name}
                    onChange={(e) => setEditor((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
                {isTwilio ? (
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      {t('whatsappTemplates.fields.contentSid')} *
                    </label>
                    <input
                      className={INPUT_CLASS}
                      placeholder="HX..."
                      value={editor.content_sid}
                      onChange={(e) => setEditor((p) => ({ ...p, content_sid: e.target.value }))}
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">
                      {t('whatsappTemplates.fields.providerTemplateName')}
                    </label>
                    <input
                      className={INPUT_CLASS}
                      value={editor.external_template_name}
                      onChange={(e) =>
                        setEditor((p) => ({ ...p, external_template_name: e.target.value }))
                      }
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    {t('whatsappTemplates.fields.bodyText')} *
                  </label>
                  <textarea
                    className={`${INPUT_CLASS} min-h-[120px]`}
                    value={editor.body_text}
                    onChange={(e) => setEditor((p) => ({ ...p, body_text: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleCreateAndActivate}
                  className={BTN_PRIMARY}
                  style={{ background: GRADIENT, boxShadow: '0 8px 20px rgba(14,165,233,0.22)' }}
                >
                  <Save className="w-4 h-4" />
                  {saving
                    ? t('whatsappSettings.saving')
                    : t('whatsappSettings.eventTemplateModal.saveAndUse')}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className={BTN_SECONDARY}
                >
                  {t('whatsappTemplates.cancel')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
